import {JsonApiDataStore, serialize} from './jsonapi-datastore-fork';
import api from 'shared/core/api';
import _ from 'lodash';

const POSITIVE_INFINITY = Number.POSITIVE_INFINITY || 2147483647;

export {DomainObject, oneOf} from './DomainObject';

export const createStore = () => {
  return new JsonApiDataStore();
};

export const getSingle = (response, type) => {
  const store = createStore();
  store.sync(response.data);
  return _.head(store.getAll(type));
};

export const fetchModels = async (url, type, options={}) => {
  const store = createStore();
  const response = await api.get(url, options);
  store.sync(response.data);
  return store.getAll(type);
};

export const fetchModel = async (url, type, options={}) => {
  const store = createStore();
  const response = await api.get(url, options);
  store.sync(response.data);
  return getSingle(response, type);
};

export const serializeModel = (plainObject, type) => {
  return serialize(plainObject, type);
};

export const canDeleteModel = (model) => {
  const methods = _.get(model, 'links.self.meta.methods');
  return methods && _.includes(methods, 'DELETE');
};

export const canPatchModel = (model) => {
  const methods = _.get(model, 'links.self.meta.methods');
  return methods && _.includes(methods, 'PATCH');
};

export const deleteModel = async (model, params = {}) => {
  const href = _.get(model, 'links.self.href');
  return await api.delete(href, {params});
};

const request = async (url, type, model, func) => {
  const payload = serializeModel(model, type);
  try {
    const response = await func(url, payload);
    return {
      model: getSingle(response, type),
      errors: {}
    };
  } catch (e) {
    if (_.get(e, 'response.status') === 409) {
      return {status: 409};
    }
    if (e.formErrors) {
      return {errors: e.formErrors, status: 422};
    }

    throw e;
  }
};

export const postModel = async (url, type, model = {}) => {
  return await request(url, type, model, api.post);
};

export const patchModel = async (url, type, model) => {
  return await request(url, type, model, api.patch);
};

function load(endpoint) {
  if (_.isString(endpoint)) {
    return api.get(endpoint);
  } else if (_.isObject(endpoint) && _.has(endpoint, 'url')) {
    return api.get(endpoint.url, {params: endpoint.params || {}});
  } else {
    throw new Error(`Endpoint must be a string or an object {url, params}. Instead received: ${endpoint}`);
  }
}

export class DomainStore {
  _repository = createStore();

  async _compose(endpoints, options = {}) {
    const _endpoints = _.isArray(endpoints) ? endpoints : [endpoints];
    const requests = _endpoints.map(endpoint => {
      if (options.failSilently === true) {
        return load(endpoint).catch(e => {
          return {
            failedSilently: true, endpoint, e
          };
        });
      }
      return load(endpoint);
    });
    const result = [];
    for (const response of await Promise.all(requests)) {
      if (response.data) {
        result.push(this._repository.syncWithMeta(response.data));
      } else {
        result.push(response);
      }
    }
    return result;
  }

  _getAll(type, filters, _class) {
    const __class = arguments.length === 2 ? filters : _class;
    const __filters = arguments.length === 3 ? filters : null;
    const models = _.filter(this._repository.getAll(type).map(i => __class ? new __class(i) : i), __filters);
    const activeModels = _.reject(models, 'deletedAt');
    return _.orderBy(activeModels, model => parseInt(model.id) || POSITIVE_INFINITY);
  }

  _getSingle(type, filters) {
    return _.head(_.filter(this._getAll(type), filters));
  }

  async __request(endpoint, type, model, func) {
    const modelToSend = _.isFunction(model.toJS) ? model.toJS() : model;
    const payload = serializeModel(modelToSend, type);

    try {
      const response = await func(endpoint, payload);
      if (response.data) {
        this._repository.sync(response.data);
      }
      return {
        model: type && response.data ? this._getSingle(type, {id: _.get(getSingle(response, type), 'id')}) : null,
        errors: {}
      };
    } catch (e) {
      if (e.formErrors) {
        return {errors: e.formErrors};
      }

      throw e;
    }
  }

  async __deleteRequest(model, params) {
    try {
      const response = await deleteModel(model, params);
      if (response.data) {
        this._repository.sync(response.data);
      }

      return {errors: {}};
    } catch (e) {
      if (e.formErrors) return {errors: e.formErrors};

      throw e;
    }
  }

  async post(endpoint, type, model = {}) {
    return this.__request(endpoint, type, model, api.post);
  }

  async patch(endpoint, type, model = {}) {
    if (_.isObject(endpoint)) {
      const _model = endpoint;
      return this.__request(_model.link('self'), _model._type, _model, api.patch);
    } else {
      return this.__request(endpoint, type, model, api.patch);
    }
  }

  async put(endpoint, type, model = {}) {
    return this.__request(endpoint, type, model, api.put);
  }

  async destroy(type, model = {}, params = {}) {
    if (_.isObject(type)) {
      const result = await this.__deleteRequest(type);
      if (_.isEmpty(result.errors)) {
        this._repository.destroy(type);
      }
      return result;
    } else {
      const result = await this.__deleteRequest(model, params);
      if (_.isEmpty(result.errors)) {
        this._repository.destroy({_type: type, id: model.id});
      }
      return result;
    }
  }

  async fetch(endpoint, type, urlParams = {}) {
    const response = await api.get(endpoint, {params: urlParams || {}});
    this._repository.sync(response.data);
    return this._getSingle(type);
  }

  getAllOrdered(type, ModelClass) {
    return _.orderBy(this._getAll(type, ModelClass), ModelClass.defaultOrderAttribute());
  }

  invalidate(type, filters) {
		this._repository.invalidate(type, filters);
  }
}
